import React, { useState } from 'react';
import { ListGroupNavItem } from './ListGroupNavComponents';
import { ListGroup } from 'react-bootstrap';
import { useAllIncidents, useCurrentPeriod } from '../IAP/hooks/incidentHooks';
import StylishNewSelect from '../DesignSystems/New/StylishNewSelect';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { updateCurrentIncident } from '../../actions/diceActions';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { useIncident } from '../IAP/hooks/incidentHooks';
import { useStreamlinePlatformFlag } from '../../utils/featureFlags';

const IncidentWorkspacePanel: React.FC<{
  navBack: () => void;
}> = ({ navBack }) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const streamlinePlatform = useStreamlinePlatformFlag();

  const { data: incident } = useIncident();
  const { data: allIncidents } = useAllIncidents();
  const { periodNumber } = useCurrentPeriod();

  const incidentChangeHandler = (incidentId: string, isNew: boolean) => {
    const currentIncidentId = String(incident?.id);
    const newIncidentId = String(incidentId);

    if (!isNew && currentIncidentId === newIncidentId) {
      return;
    }
    if (!allIncidents?.length) {
      return;
    }
    const selectedIncident = allIncidents.find(
      (incident) => String(incident?.id) === newIncidentId
    );
    if (!selectedIncident?.id) {
      return;
    }
    // Replace old incident ID with new one in current path
    const newPath = location.pathname.replace(
      `/iap/${currentIncidentId || 'undefined'}`,
      `/iap/${selectedIncident.id}`
    );

    // If the path doesn't contain /iap/, default to dashboard
    const targetPath = newPath.includes('/iap/')
      ? newPath
      : `/iap/${selectedIncident.id}/dashboard`;

    dispatch(updateCurrentIncident(selectedIncident));

    const newUrl = location.search
      ? `${targetPath}${location.search}`
      : targetPath;

    navigate(newUrl);
    setShowDropdown(false);
  };

  const zeroStateListGroup = (
    <ListGroup>
      <div className="list-group-title">Manage Incident</div>
      <ListGroupNavItem
        isDisabled={true}
        to={`/none`}
        icon="checklist"
        label="Planning"
      />
      <ListGroupNavItem
        isDisabled={true}
        showArrow={true}
        to={`/none`}
        icon="commit"
        label="Operations"
      />
      <ListGroupNavItem
        isDisabled={true}
        to={`/none`}
        icon="file_present"
        label="Documents"
      />
      <ListGroupNavItem
        isDisabled={true}
        to={`/none`}
        icon="bar_chart"
        label="Analytics"
      />
      <ListGroupNavItem
        isDisabled={true}
        to={`/none`}
        icon="settings"
        label="Settings"
      />
    </ListGroup>
  );
  const defaultListGroup = (
    <ListGroup>
      <ListGroupNavItem
        to={`/iap/${incident?.id}/dashboard`}
        icon={periodNumber === 0 ? 'description' : 'dashboard'}
        label={periodNumber === 0 ? 'ICS-201 Workspace' : 'Planning-P'}
        end
      />
      <ListGroupNavItem
        to={`/iap/${incident?.id}/calendar`}
        icon="calendar_month"
        label="Calendar"
      />
      <ListGroupNavItem
        to={`/iap/${incident?.id}/notifications`}
        icon="notifications"
        label="Notifications"
      />
      <ListGroupNavItem
        to={`/iap/${incident?.id}/resources`}
        label="Resources"
        icon="inventory_2"
      />
      <ListGroupNavItem
        to={`/iap/${incident?.id}/roster`}
        icon="people"
        label="Incident Roster"
      />
      {!streamlinePlatform && (
        <ListGroupNavItem
          to={`/iap/${incident?.id}/forms`}
          label="ICS Forms"
          icon="insert_drive_file"
        />
      )}
      {!streamlinePlatform && (
        <ListGroupNavItem
          to={`/iap/${incident?.id}/activity-logs`}
          icon="history"
          label="Activity Logs"
        />
      )}
      <ListGroupNavItem
        to={`/iap/${incident?.id}/reports`}
        icon="flag"
        label="Reports"
      />
      {/* Archiving but not deleting */}
      {/* <ListGroupButtonItem
        onClick={() => toggleOperations()}
        open={operationsOpen}
        icon="commit"
        label="Operations"
      />
      <Collapse in={operationsOpen}>
        <ListGroupNavItem
          to={`/iap/${incident?.id}/actions`}
          label="Actions"
          secondary
        />
        <ListGroupNavItem
          to={`/iap/${incident?.id}/assignments`}
          label="Assignments"
          secondary
        />
        <ListGroupNavItem
          to={`/iap/${incident?.id}/map`}
          label="Full Map"
          secondary
        />
      </Collapse> */}
      <ListGroupNavItem
        to={`/iap/${incident?.id}/profile`}
        icon="person"
        label="Incident Profile"
      />
      <ListGroupNavItem
        to={`/iap/${incident?.id}/files`}
        icon="folder"
        label="Files"
      />
      <ListGroupNavItem
        to={`/iap/${incident?.id}/settings`}
        icon="settings"
        label="Settings"
      />
    </ListGroup>
  );
  return (
    <nav aria-label="secondary sidebar" className="second-panel">
      <div className="second-panel-back align-items-start gap-3">
        <div className="d-flex align-items-center">
          <StylishNewButton
            className="button button--link text-white me-2"
            onClick={navBack}
          >
            <i className="material-symbols-outlined">arrow_back</i>
          </StylishNewButton>
          <i className="material-symbols-outlined me-1 navbar-header">
            electric_bolt
          </i>
          <span className="navbar-header">Incident Workspace</span>
        </div>
      </div>
      <div className="navbar-title ps-4 pe-3 fs-4">
        {showDropdown || incident?.id === undefined ? (
          <StylishNewSelect
            classes={'secondary sml'}
            options={
              allIncidents
                ? allIncidents
                    .filter((incident) => incident.archived === 0)
                    .map((incident) => ({
                      label: incident.name,
                      value: incident.id,
                    }))
                : []
            }
            value={
              incident?.id
                ? {
                    value: incident.id,
                    label: incident.name,
                  }
                : null
            }
            placeholder="Select an Incident Workspace"
            onChange={(e) => incidentChangeHandler(e.value, false)}
          />
        ) : (
          incident?.name
        )}
      </div>
      {incident?.id !== undefined && (
        <div className="ps-3 pe-3">
          <StylishNewButton
            className="button button--link"
            onClick={() => setShowDropdown(!showDropdown)}
          >
            Switch Workspace
          </StylishNewButton>
        </div>
      )}
      {incident?.id === undefined ? zeroStateListGroup : defaultListGroup}
    </nav>
  );
};

export default IncidentWorkspacePanel;
